import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  fetchUsers,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFailure,
  addUser,
  addUserStart,
  addUserSuccess,
  addUserFailure,
  editUser,
  editUserStart,
  editUserSuccess,
  editUserFailure,
  deleteUser,
  deleteUserStart,
  deleteUserSuccess,
  deleteUserFailure,
  fetchUserById,
  fetchUserByIdStart,
  fetchUserByIdSuccess,
  fetchUserByIdFailure,
  resendEmail,
  resendEmailStart,
  resendEmailSuccess,
  resendEmailFailure,
  fetchProfile,
  fetchProfileStart,
  fetchProfileSuccess,
  fetchProfileFailure,
  changePassword,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
} from "../slices/usersSlice";
import { makeRequest } from "../../utils/api";
import { User, UserPaginatedResponse } from "../../types/users";
import { extractErrorMessage } from "../../utils/helpers";
import { toast } from "react-toastify";

function* fetchUsersSaga(
  action: PayloadAction<{ page: number; rowsPerPage: number; sortBy?: string; sortOrder?: number }>
): Generator<any, void, any> {
  try {
    yield put(fetchUsersStart());
    const skip = action.payload.page * action.payload.rowsPerPage;
    const limit = action.payload.rowsPerPage;
    const sortBy = action.payload.sortBy || "created_at";
    const sortOrder = action.payload.sortOrder !== undefined ? action.payload.sortOrder : -1;

    const response: UserPaginatedResponse = yield call(
      makeRequest,
      "get",
      `/users/?skip=${skip}&limit=${limit}&sort_by=${sortBy}&sort_order=${sortOrder}`
    );
    yield put(fetchUsersSuccess(response));
  } catch (error: any) {
    yield put(fetchUsersFailure(extractErrorMessage(error)));
  }
}

function* addUserSaga(
  action: PayloadAction<
    Omit<User, "id" | "created_at" | "updated_at" | "registered_at">
  >
): Generator<any, void, any> {
  try {
    yield put(addUserStart());
    const response: User = yield call(
      makeRequest,
      "post",
      "/users/",
      action.payload
    );
    yield put(addUserSuccess(response));
    toast.success(response.message ?? "User added successfully!");
  } catch (error: any) {
    yield put(addUserFailure(extractErrorMessage(error)));
  }
}

function* editUserSaga(
  action: PayloadAction<{
    userId: string;
    userData: Partial<
      Omit<
        User,
        | "id"
        | "created_at"
        | "updated_at"
        | "registered_at"
        | "email"
        | "verified_email"
      >
    >;
  }>
): Generator<any, void, any> {
  try {
    yield put(editUserStart());
    const response: User = yield call(
      makeRequest,
      "put",
      `/users/${action.payload.userId}`,
      action.payload.userData
    );
    yield put(editUserSuccess(response));
    toast.success(response.message ?? "User updated successfully!");
  } catch (error: any) {
    yield put(editUserFailure(extractErrorMessage(error)));
  }
}

function* deleteUserSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    yield put(deleteUserStart());
    yield call(makeRequest, "delete", `/users/${action.payload}`);
    yield put(deleteUserSuccess(action.payload));
    toast.success("User deleted successfully!");
  } catch (error: any) {
    yield put(deleteUserFailure(extractErrorMessage(error)));
  }
}

function* fetchUserByIdSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    yield put(fetchUserByIdStart());
    const response: User = yield call(
      makeRequest,
      "get",
      `/users/${action.payload}`
    );
    yield put(fetchUserByIdSuccess(response));
  } catch (error: any) {
    yield put(fetchUserByIdFailure(extractErrorMessage(error)));
  }
}

// ✅ NEW: Resend email saga
function* resendEmailSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    yield put(resendEmailStart());
    const response = yield call(
      makeRequest,
      "post",
      `/users/resend-invitation/${action.payload}`
    );
    yield put(resendEmailSuccess(response));
    toast.success(
      response.message ?? "Verification email resent successfully!"
    );
  } catch (error: any) {
    yield put(resendEmailFailure(extractErrorMessage(error)));
  }
}
function* fetchProfileSaga(): Generator<any, void, any> {
  try {
    yield put(fetchProfileStart());
    const response: User = yield call(makeRequest, "get", "/users/me");
    yield put(fetchProfileSuccess(response));
  } catch (error: any) {
    yield put(fetchProfileFailure(extractErrorMessage(error)));
  }
}

function* changePasswordSaga(
  action: PayloadAction<{ old_password: string; new_password: string }>
): Generator<any, void, any> {
  try {
    yield put(changePasswordStart());
    const response = yield call(
      makeRequest,
      "post",
      "/users/change-password",
      action.payload
    );
    yield put(changePasswordSuccess(response));
    toast.success(response.message ?? "Password changed successfully!");
  } catch (error: any) {
    yield put(changePasswordFailure(extractErrorMessage(error)));
  }
}

export default function* usersSaga(): Generator<any, void, any> {
  yield takeLatest(fetchUsers.type, fetchUsersSaga);
  yield takeLatest(addUser.type, addUserSaga);
  yield takeLatest(editUser.type, editUserSaga);
  yield takeLatest(deleteUser.type, deleteUserSaga);
  yield takeLatest(fetchUserById.type, fetchUserByIdSaga);
  yield takeLatest(resendEmail.type, resendEmailSaga); // ✅ NEW
  yield takeLatest(fetchProfile.type, fetchProfileSaga);
  yield takeLatest(changePassword.type, changePasswordSaga);
}
